// Rilevazione dell'ambiente di esecuzione. Il ramo browser vince su Node:
// sotto jsdom (test) esistono sia `window` sia `process`, e il comportamento
// corretto è quello browser.

/// `true` in un ambiente browser (o browser-like, es. jsdom).
export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

/// `true` in Node.js puro (senza DOM).
export function isNode(): boolean {
  return (
    !isBrowser() &&
    typeof process !== 'undefined' &&
    typeof process.versions?.node === 'string'
  )
}

/// `contexts.runtime` dell'evento: `{name:'browser'}` oppure
/// `{name:'node', version: process.version}`.
export function runtimeContext(): Record<string, unknown> {
  if (isNode()) return { name: 'node', version: process.version }
  return { name: 'browser' }
}

/// Hostname della macchina in Node (`os.hostname()`) → campo `server_name`
/// dell'evento, in parità con la gem Ruby. `undefined` nel browser, se `os` è
/// irraggiungibile o su hostname vuoto (non emettere un campo vuoto).
///
/// Guardia dynamic-require browser-safe: `os` NON è importato staticamente —
/// romperebbe il bundle browser (l'IIFE `<script>` non ha i builtin Node). È
/// risolto a runtime via `process.getBuiltinModule`, che il bundler non linka e
/// che il browser non raggiunge mai (gate `isNode`). Richiede Node ≥20.16 —
/// il minimo dichiarato in `engines` (vedi `package.json`).
export function serverName(): string | undefined {
  if (!isNode()) return undefined
  try {
    const host = nodeOs()?.hostname?.()
    return typeof host === 'string' && host.length > 0 ? host : undefined
  } catch {
    return undefined
  }
}

/// Una release semver valida: `v` iniziale opzionale, `MAJOR.MINOR.PATCH`, con
/// suffisso pre-release/build opzionale. Stessa regola della gem Ruby (CYRB-9):
/// scarta i valori non-versione (`unknown`, nomi di branch) che non devono finire
/// come release.
const SEMVER_RELEASE = /^v?\d+\.\d+\.\d+([-+.].+)?$/

/// Auto-rileva la release dal deploy — SOLO su runtime Node reale (SSR/backend),
/// in parità tag-first con la gem Ruby. Il tag semver del deploy ha la precedenza
/// (`APP_GIT_TAG` poi `GIT_TAG`, accettati solo se semver validi), poi lo SHA del
/// deploy (`KAMAL_VERSION` poi `GIT_SHA`) come fallback.
///
/// `undefined` nel browser: il gate `isNode()` richiede `process.versions.node` e
/// esclude window/jsdom, così un `process` polyfillato nel bundle browser non
/// attiva l'auto-detect (nessun accesso alle variabili di processo lato browser).
/// SDK isomorfo: si leggono solo le ENV, MAI un subprocess git. Nel browser la
/// release va passata esplicita nelle options (build-time inject).
export function detectRelease(): string | undefined {
  if (!isNode()) return undefined
  const env = process.env
  const tag = env.APP_GIT_TAG || env.GIT_TAG
  if (tag && SEMVER_RELEASE.test(tag)) return tag
  return env.KAMAL_VERSION || env.GIT_SHA || undefined
}

interface NodeOs {
  hostname?: () => string
}

interface ProcessWithBuiltin {
  getBuiltinModule?: (id: string) => unknown
}

// Modulo `os` di Node senza import statico (il bundle browser non deve contenere
// 'os'). `getBuiltinModule` è l'API Node per accedere ai builtin da librerie
// universali; garantita da `engines.node >=20.16`. Il guard resta come difesa in
// profondità: `undefined` (mai un crash) su un runtime che non la esponesse.
function nodeOs(): NodeOs | undefined {
  const getBuiltin = (process as ProcessWithBuiltin).getBuiltinModule
  if (typeof getBuiltin !== 'function') return undefined
  return getBuiltin('os') as NodeOs
}
